/**
 * Curated Google Fonts / web-safe families for the page-builder font picker.
 * Not a full Google catalog — searchable recommended set + common extras.
 */

/** Popular Google Fonts (and close web-safe aliases) shown in the picker. */
export const GOOGLE_FONTS_CATALOG: readonly string[] = [
	"Inter",
	"Roboto",
	"Open Sans",
	"Lato",
	"Montserrat",
	"Source Sans 3",
	"Nunito",
	"Poppins",
	"Raleway",
	"Oswald",
	"Merriweather",
	"Playfair Display",
	"PT Sans",
	"PT Serif",
	"Roboto Slab",
	"Roboto Mono",
	"Ubuntu",
	"Noto Sans",
	"Noto Serif",
	"Work Sans",
	"DM Sans",
	"DM Serif Display",
	"IBM Plex Sans",
	"IBM Plex Serif",
	"IBM Plex Mono",
	"Space Grotesk",
	"Space Mono",
	"Outfit",
	"Manrope",
	"Figtree",
	"Plus Jakarta Sans",
	"Libre Baskerville",
	"Libre Franklin",
	"Source Serif 4",
	"Source Code Pro",
	"Fira Sans",
	"Fira Code",
	"Inconsolata",
	"Josefin Sans",
	"Mulish",
	"Karla",
	"Rubik",
	"Barlow",
	"Archivo",
	"Cabin",
	"Crimson Text",
	"EB Garamond",
	"Cormorant Garamond",
	"Bitter",
	"Lexend",
	"Sora",
	"Titillium Web",
	"Quicksand",
	"Hind",
	"Heebo",
	"Assistant",
	"Red Hat Display",
	"Red Hat Text",
	"Public Sans",
	"Fraunces",
	"Literata",
	"Newsreader",
	"Instrument Sans",
	"Instrument Serif",
] as const;

/** Subset when “Show recommended fonts only” is on. */
export const RECOMMENDED_GOOGLE_FONTS: ReadonlySet<string> = new Set([
	"Inter",
	"Roboto",
	"Open Sans",
	"Source Sans 3",
	"Montserrat",
	"Lato",
	"Poppins",
	"Merriweather",
	"Playfair Display",
	"IBM Plex Sans",
	"DM Sans",
	"Work Sans",
	"Nunito",
	"Roboto Slab",
	"Noto Sans",
	"Space Grotesk",
	"Outfit",
	"Manrope",
	"Source Serif 4",
	"Fira Code",
]);

/**
 * Filter the catalog by search query and optional recommended-only flag.
 * Match is case-insensitive substring on the family name.
 */
export type GoogleFontWeightSpec = {
	/** css2 `wght` values Google accepts for this family. */
	weights: readonly number[];
	/** Whether the family has an italic axis. */
	italic: boolean;
};

const FULL_VARIABLE_ITALIC: GoogleFontWeightSpec = {
	weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
	italic: true,
};

/**
 * Families whose Google css2 axis is narrower than a full 100–900 italic face.
 * Requesting a missing static weight makes Google return 400 for that family URL.
 */
const GOOGLE_FONT_WEIGHT_OVERRIDES: Record<string, GoogleFontWeightSpec> = {
	"Crimson Text": { weights: [400, 600, 700], italic: true },
	"DM Serif Display": { weights: [400], italic: true },
	"Fira Code": { weights: [300, 400, 500, 600, 700], italic: false },
	"Instrument Serif": { weights: [400], italic: true },
	"Josefin Sans": {
		weights: [100, 200, 300, 400, 500, 600, 700],
		italic: true,
	},
	"Libre Baskerville": { weights: [400, 700], italic: true },
	Oswald: { weights: [200, 300, 400, 500, 600, 700], italic: false },
	"PT Sans": { weights: [400, 700], italic: true },
	"PT Serif": { weights: [400, 700], italic: true },
	"Roboto Mono": {
		weights: [100, 200, 300, 400, 500, 600, 700],
		italic: true,
	},
	"Space Mono": { weights: [400, 700], italic: true },
};

/** Supported weights + italic for every catalog family. */
export const GOOGLE_FONT_WEIGHTS: Record<string, GoogleFontWeightSpec> =
	Object.fromEntries(
		GOOGLE_FONTS_CATALOG.map((family) => [
			family,
			GOOGLE_FONT_WEIGHT_OVERRIDES[family] ?? FULL_VARIABLE_ITALIC,
		])
	);

export function filterGoogleFonts(
	query: string,
	recommendedOnly: boolean
): string[] {
	const q = query.trim().toLowerCase();
	let list = GOOGLE_FONTS_CATALOG;
	if (recommendedOnly) {
		list = list.filter((name) => RECOMMENDED_GOOGLE_FONTS.has(name));
	}
	if (!q) {
		return [...list];
	}
	return list.filter((name) => name.toLowerCase().includes(q));
}
